// Catalog.ID Client — Shared Finalize Sequence (finalize-at-print)
// ============================================================================
// The DOM-free half of the wizards' finalize step (registration-restructure
// §3.2 + §13.1). Every registration flow ends the same way once the entropy
// ceremony has produced the final values, so the sequence lives here once:
//
//   1. append the ceremony's `xrpl_master` entry — the Recovery Kit's
//      `recovery_kit` entry, when the member opted into a kit, and, on an
//      ORGANIZATION bag only, the ceremony's `bitcash_wallet` key — to the
//      in-memory bag, each unless the bag already carries one (resume rule,
//      §13.1 / §16.6) — and the account's `derive_root` (cid-encrypted-storage
//      §5.1) when the bag holds none, so no module ever has to mint one at a
//      supersecret prompt later;
//   2. re-encrypt the whole bag under the FINAL supersecret and write it
//      (delete-all + recreate: PUT /api/account/keybag, or the org-scoped
//      endpoint for an organization's own bag);
//   3. copy the DEVICE-STORABLE entries added in step 1 into device storage
//      under the device DEK — device setup ran before the print step and knows
//      nothing about a key minted at finalize (§11 trap). Best-effort: a device
//      that cannot take the copy is reported, never fatal. The Recovery Kit
//      entry is deliberately excluded (`isDeviceStorableKeyId`, §16.5): its
//      coefficients plus one printed sheet ARE the supersecret;
//   3b. on a PERSONAL or DELEGATE account, adopt-or-create the member's wallet
//      document in `cid/bitcash-wallets` (cid-encrypted-storage §7.2). D5 is
//      relaxed: a personal wallet key is PIN-level and its list is a blob, not
//      a bag entry. The resume rule is the sharpest of them all — the print
//      step releases the voucher's bits into the wallet the ACCOUNT holds, so a
//      second key would print a wallet nobody funded. AFTER the bag write,
//      because the blob key is derived from the `derive_root` that write just
//      persisted, and NEVER fatal: a blob the node refuses is reported as
//      `walletBlobMissing` and the printed key is still the one the bits go to;
//   4. install the final auth hash (POST /api/account/set-authhash) — LAST of
//      the credential writes, because from here the throwaway password is dead;
//   5. store the ceremony's Authenticator Key on the node (PUT
//      /api/account/totp/key, step-up `totp-manage`, §15.6) — after the
//      password, because it is the least destructive write and must not be what
//      strands a half-finalized account. RESUME RULE (load-bearing): a second
//      PUT would rotate the secret on the node and void a sheet that has already
//      been printed, so a caller that knows the node already holds this secret
//      passes `totpAlreadyStored` and no PUT happens.
//
// What stays OUTSIDE: the Account Unlock Sheet issue (wizard-specific and
// must not re-issue on a reprint), the recovery kit, the booklet and the
// type-back gate. Callers keep those.
//
// ── Interface for M13 (delegate activation wizard rebuild) ──────────────────
// The activation wizard reuses this module as-is:
//
//   const result = await finalizeCredentials({
//       cid: delegateCid,          // the account being finalized
//       bag: delegateKeyBag,       // in-memory bag (NOT mutated)
//       supersecret: final.supersecret,
//       password: final.password,  // omit to skip set-authhash entirely
//       xrplSeedEntropy: final.xrplSeedEntropy,   // from the ceremony
//       device: deviceCopyTarget(cid, shared),    // DEK captured at device setup
//       onStatus: m => { statusEl.textContent = m; },
//   });                                           // default deps = account-scoped
//
// and then prints `result.xrplSeedEntropy` (which is the bag's entry, not
// necessarily the ceremony's), stores `result.bag` back into its resume stash
// and calls `keys.load(result.bag)`. Pass {@link orgFinalizeDeps} instead of
// the default deps to write an ORGANIZATION's bag (org-scoped endpoint, the
// challenge signed with the org's own main_signing_key).

import {
    encryptKeyBag, getKeyBagEntry, isDeviceStorableKeyId,
    type EncryptedKeyBagData, type KeyBag, type KeyBagEntry,
} from '../crypto/keybag';
import { RECOVERY_KIT_KEY_ID, activeRecoveryKitEntry } from '../crypto/recovery-kit-entry';
import {
    activeBitcashWalletEntries, bitcashWalletBagEntry,
    decodeBitcashWalletEntry, nextBitcashWalletKeyIndex,
} from '../crypto/bitcash-wallet-entry';
import {
    BITCASH_WALLETS_BLOB, activeWallets, appendWallet, emptyWalletsDocument,
    type BitcashWalletsDocument,
} from '../crypto/bitcash-wallets-doc';
import { defaultCidBlobDeps, readCidBlob, writeCidBlob } from '../state/cid-blobs';
import { decodeWalletsDocument } from '../crypto/bitcash-wallets-doc';
import { toCompact, verifyWalletKey } from '../crypto/bitcash';
import {
    XRPL_MASTER_KEY_ID, XRPL_MASTER_ALGORITHM, XRPL_SEED_ENTROPY_LEN,
} from '../crypto/xrpl';
import {
    DERIVE_ROOT_KEY_ID, DERIVE_ROOT_ALGORITHM, generateDeriveRoot,
} from '../crypto/derive';
import { TOTP_SECRET_LENGTH, base32Encode } from '../crypto/totp';
import { computeAuthHash } from '../crypto/credentials';
import { signHybrid, wrapperToJson } from '../crypto/signing';
import * as aes from '../crypto/aes';
import * as accountApi from '../api/account';
import * as organizationApi from '../api/organization';
import * as deviceStorage from '../state/device-storage';
import { isDeviceKeyChosen, type DeviceKeyManifest } from '../state/device-key-selection';
import { signOrgStepUp, signStepUp } from './step-up';

/** What a wallet is called when the caller says nothing. Short, because it is a
 *  column heading in the Settings list as much as a name. */
export const DEFAULT_BITCASH_WALLET_LABEL = 'My wallet';

/** Where the device copy of a newly minted key goes. */
export interface FinalizeDeviceTarget {
    /** The account partition in device storage — the DELEGATE's cid in the org
     *  wizard, since the device belongs to the delegate. */
    cid: string;
    /** The device encryption key (DEK) generated at device setup, kept in the
     *  wizard's in-memory state for exactly this reason. */
    encryptionKey: Uint8Array;
    /**
     * Which keys the member chose to keep on this device
     * (device-setup-restructure §7.3). Finalize mints keys AFTER device setup
     * ran, so it is the one path that could write a key this device was never
     * meant to hold. A DECLINED id is skipped and reported as neither stored
     * nor missing — it is absent on purpose, not a gap.
     *
     * Absent/null means no selection was carried through, which reads as
     * "everything chosen" — today's behaviour, and the behaviour of a device
     * set up before the picker. The wizards plumb the real manifest through
     * `shared` in M5.
     */
    manifest?: DeviceKeyManifest | null;
}

// ── Carrying the device DEK from device setup to finalize ───────────────────
// Device setup (components/device-setup.ts) generates the DEK, encrypts the bag
// with it and hands it to the server; nothing kept it afterwards. The finalize
// step runs later in the SAME page lifetime, so the wizards stash it in their
// `shared` map on the way past.
//
// That map is also what the onboarding stash persists (unencrypted, by design —
// it already carries the plaintext key bag and the throwaway supersecret), so
// the DEK survives a crash-resume too and the resume path needs no PIN unlock.
// The stash is wiped at the type-back gate along with everything else.

const DEVICE_KEY_FIELD = 'deviceEncryptionKey';
const DEVICE_KEY_CID_FIELD = 'deviceEncryptionKeyCid';
/** The picker's selection (device-setup-restructure §8), remembered beside the
 *  DEK because the two describe the same device copy and must never diverge. */
const DEVICE_MANIFEST_FIELD = 'deviceKeyManifest';

/** Remember the DEK device setup just installed (base64, as the callback
 *  reports it) for the account whose device partition it protects, together
 *  with the key selection the member made on the same screen (§8).
 *
 *  The manifest is written on EVERY call, cleared when none is given: a second
 *  pass through device setup replaces the DEK, and a selection belonging to the
 *  previous one would describe a copy that no longer exists. */
export function rememberDeviceKey(
    shared: Record<string, unknown>, cid: string, deviceEncryptionKeyB64: string,
    manifest?: DeviceKeyManifest | null,
): void {
    shared[DEVICE_KEY_FIELD] = deviceEncryptionKeyB64;
    shared[DEVICE_KEY_CID_FIELD] = cid;
    if (manifest) shared[DEVICE_MANIFEST_FIELD] = manifest;
    else delete shared[DEVICE_MANIFEST_FIELD];
}

/** The device copy target for {@link finalizeCredentials}, or null when this
 *  device holds no keys ("no local storage" mode, or a resume that lost it). */
export function deviceCopyTarget(shared: Record<string, unknown>): FinalizeDeviceTarget | null {
    const keyB64 = shared[DEVICE_KEY_FIELD];
    const cid = shared[DEVICE_KEY_CID_FIELD];
    if (typeof keyB64 !== 'string' || !keyB64 || typeof cid !== 'string' || !cid) return null;
    try {
        return {
            cid,
            encryptionKey: aes.fromBase64(keyB64),
            manifest: sharedDeviceKeyManifest(shared[DEVICE_MANIFEST_FIELD]),
        };
    } catch {
        return null;
    }
}

/** The remembered manifest, or null when there is none or it is not one.
 *
 *  `shared` is persisted as plain JSON by the onboarding stash, so anything
 *  here may have been written by an older build or hand-edited. Same safe
 *  failure as `readDeviceKeyManifest`: an unreadable selection means "no
 *  selection", which holds MORE keys than it must — never fewer. */
function sharedDeviceKeyManifest(value: unknown): DeviceKeyManifest | null {
    if (typeof value !== 'object' || value === null) return null;
    const m = value as Partial<DeviceKeyManifest>;
    if (m.v !== 1) return null;
    if (!isStringList(m.chosen) || !isStringList(m.declined)) return null;
    return { v: 1, chosen: m.chosen, declined: m.declined };
}

function isStringList(v: unknown): v is string[] {
    return Array.isArray(v) && v.every(x => typeof x === 'string');
}

export interface FinalizeCredentialsParams {
    /** The account whose bag and auth hash are being finalized. */
    cid: string;
    /** The bag as held in memory. Never mutated — use the returned bag. */
    bag: KeyBag;
    /** The FINAL supersecret from the ceremony; the bag is re-encrypted under it. */
    supersecret: string;
    /** The FINAL password. Omit to skip set-authhash — the organization account
     *  has no user-facing password (§5, org variant). */
    password?: string;
    /** Registration finalize of an ORGANIZATION (docs/cid-governance.md M15):
     *  retire the throwaway password the wizard set, in the password's place in
     *  the sequence. Only the org wizard asks; a later org-bag rewrite (a
     *  recovery-kit reissue) does not. Needs deps with `retirePassword`. */
    retirePassword?: boolean;
    /** 16 bytes of XRPL family-seed entropy from the ceremony. Ignored when the
     *  bag already carries `xrpl_master`; omit to mint nothing at all. */
    xrplSeedEntropy?: Uint8Array | null;
    /** 20 bytes of Authenticator Key (TOTP) secret from the ceremony (§15.6).
     *  Omit to store nothing at all — the organization half, and any resume from
     *  a pre-round-8 stash. */
    totpSecret?: Uint8Array | null;
    /** RESUME RULE (§15.8): true when the node ALREADY holds `totpSecret` — the
     *  stash recorded it on a previous run. No PUT happens, and the result still
     *  reports it stored, because the printed sheet must show what the node
     *  holds and a re-PUT would void the sheet already on paper. */
    totpAlreadyStored?: boolean;
    /** The Recovery Kit's `recovery_kit` entry (§16.6), minted BEFORE this call
     *  so the set lands in the bag the node stores AND in the bundle the kit
     *  exports. Appended only when the bag holds no ACTIVE one — the same resume
     *  rule `xrpl_master` has, and for the same reason: a resumed finalize must
     *  print the set that is already on the server, never a second one that
     *  would not combine with sheets already on paper. Omit when the member
     *  opted out of a kit. */
    recoveryKitEntry?: KeyBagEntry | null;
    /** The ceremony's bitcash wallet key, COMPACT or display form
     *  (registration-wallets §4.9). Filed unless the account already holds an
     *  ACTIVE one — the resume rule, and the strictest of them all: the print
     *  step releases the voucher's bits into the wallet the ACCOUNT holds, so a
     *  second key would print a wallet nobody funded. Omit to mint nothing.
     *
     *  WHERE it is filed is `deps.walletCustody`: the `cid/bitcash-wallets`
     *  blob for a personal or delegate account (cid-encrypted-storage §7.2), a
     *  `bitcash_wallet` bag entry for an ORGANIZATION, whose delegates reach it
     *  through the org key wraps and have no usable derive root (§5.1). On the
     *  organization path this is the ORGANIZATION's wallet and the deps are
     *  {@link orgFinalizeDeps}; the founding delegate's own wallet rides the
     *  delegate half of the same ceremony (§4.13). */
    bitcashWalletKey?: string | null;
    /** What the member calls the wallet, at most 64 characters. Defaults to
     *  {@link DEFAULT_BITCASH_WALLET_LABEL}. */
    bitcashWalletLabel?: string;
    /** Device copy target, or null when this device holds no keys (the "no
     *  local storage" mode, or a resume that lost the DEK). */
    device?: FinalizeDeviceTarget | null;
    /** Progress messages for the wizard's status line. */
    onStatus?: (message: string) => void;
}

export interface FinalizeCredentialsResult {
    /** The bag as written: the caller's entries plus `xrpl_master` when this
     *  call added it. Store it back into the wizard state and `keys.load()` it. */
    bag: KeyBag;
    /** The XRPL entropy the bag now carries — the freshly minted one, or the
     *  one that was already there on a resume. PRINT THIS, never the ceremony's
     *  value (§13.1). Null when the bag has no XRPL key. */
    xrplSeedEntropy: Uint8Array | null;
    /** True only when this call added the entry. */
    xrplMinted: boolean;
    /** True only when this call minted the account's `derive_root` (§5.1). False
     *  on a resume that adopted the one the bag already carried. */
    deriveRootMinted: boolean;
    /** The `recovery_kit` entry the bag now holds — the one this call appended,
     *  or the one that was already there on a resume. BUILD THE KIT FROM THIS,
     *  never from fresh randomness (§16.6). Null when the account has no kit. */
    recoveryKitEntry: KeyBagEntry | null;
    /** The bitcash wallet the ACCOUNT now holds — the one this call filed, or
     *  the one that was already there on a resume. RELEASE THE BITS INTO THIS,
     *  and print THIS, never the ceremony's value (§4.5). Null when the account
     *  has no wallet. `keyIndex` is the document's `slot` on the blob path and
     *  the bag entry's `keyIndex` on the organization path; the two have always
     *  meant the same thing (cid-encrypted-storage §7.2). */
    bitcashWallet: { key: string; keyIndex: number } | null;
    /** True when the account's wallet document could not be read or written —
     *  the blob path only. The account is FINE and {@link bitcashWallet} still
     *  names the key the bits are released into and the kit prints, so the
     *  member keeps a funded wallet on paper; what is missing is the durable
     *  list, which they can rebuild with *Add wallet…* in Settings. Reported
     *  the way {@link deviceCopyMissing} is, and for the same reason: a
     *  half-finalized account is worse than a warning. */
    walletBlobMissing: boolean;
    /** Key ids copied into device storage by this call. */
    deviceStoredKeyIds: string[];
    /** True when entries were added to the bag but could NOT be copied to this
     *  device (no DEK, or the write failed). The account is fine — the keys are
     *  in the server bag — but the first use of that key on this device will
     *  ask for the supersecret. */
    deviceCopyMissing: boolean;
    /** True when the node holds this run's Authenticator Key — whether this call
     *  PUT it or a previous run already did (§15.6). The wizard stashes this and
     *  treats it as the fact that permits PRINTING the sheet; false means the
     *  kit prints no Authenticator Key sheet. */
    totpStored: boolean;
}

/** The three writes the sequence performs, injectable so the organization half
 *  can swap the bag endpoint and so the ordering is unit-testable. */
export interface FinalizeCredentialsDeps {
    /**
     * Where a `bitcashWalletKey` this call files is stored.
     *
     * `'blob'` — personal and delegate accounts: `cid/bitcash-wallets`, under a
     * key derived from the account's own `derive_root` (cid-encrypted-storage
     * §7.2). `'bag'` — an ORGANIZATION: the `bitcash_wallet` entry, entitled to
     * owner and treasurer and reached through the org key wraps, because an
     * organization has no derive root its delegates can use (§5.1).
     *
     * An explicit field rather than "call the blob deps and see if they throw":
     * the difference decides where a member's money key lives, and that is not
     * something to infer from an exception.
     */
    walletCustody: 'blob' | 'bag';
    /** Persist the re-encrypted bag (delete-all + recreate). */
    replaceKeyBag(encrypted: EncryptedKeyBagData): Promise<void>;
    /** Compute and install the account's new auth hash. */
    setAuthHash(password: string, cid: string): Promise<void>;
    /**
     * Retire the account's password when the finalize installs none — the
     * ORGANIZATION half (docs/cid-governance.md M15): the throwaway the
     * registration wizard set is replaced by an unusable hash, so a copy of it
     * is worth nothing anywhere. Absent on the account deps, whose finalize
     * always installs a password.
     */
    retirePassword?(): Promise<void>;
    /** Write one bag entry into this device's storage under the DEK. */
    storeDeviceEntry(cid: string, entry: KeyBagEntry, deviceEncryptionKey: Uint8Array): Promise<void>;
    /** Store the Authenticator Key on the node (§15.6). */
    storeTotpSecret(secret: Uint8Array): Promise<void>;
    /**
     * Read the account's wallet document, or null when it has none.
     *
     * The derive root is passed IN rather than read from `state/keys`: at this
     * point the bag carrying it has been written but the wizard has not yet
     * called `keys.load`, so the in-memory bag would answer `cid-locked` for a
     * root this function is holding in its hand.
     */
    readWalletsBlob(
        root: Uint8Array,
    ): Promise<{ doc: BitcashWalletsDocument; generation: number } | null>;
    /** Write the wallet document, compare-and-set on `ifGeneration` (0 creates). */
    writeWalletsBlob(
        root: Uint8Array, doc: BitcashWalletsDocument, ifGeneration: number,
    ): Promise<void>;
}

/** The vault's own blob calls, with the derive root handed over explicitly. */
function walletBlobCalls(root: Uint8Array) {
    return { ...defaultCidBlobDeps, getRoot: () => root };
}

/** Account-scoped writers: the step-up-gated personal/delegate endpoints. */
export const accountFinalizeDeps: FinalizeCredentialsDeps = {
    walletCustody: 'blob',
    async replaceKeyBag(encrypted) {
        const challengeSignature = await signStepUp('replace-keybag');
        await accountApi.replaceKeyBag({
            entries: encrypted.entries,
            keySalt: encrypted.keySalt,
            challengeSignature,
        });
    },
    async setAuthHash(password, cid) {
        const newAuthHash = await computeAuthHash(password, cid);
        const challengeSignature = await signStepUp('set-authhash');
        // The swap locks every device of the account but the one it runs on
        // (docs/cid-governance.md M15a): name this one, the device the wizard
        // just set up, so it is not asking for the supersecret next time.
        const deviceToken = deviceStorage.getDeviceToken(cid);
        await accountApi.setAuthHash(deviceToken
            ? { newAuthHash, challengeSignature, deviceToken }
            : { newAuthHash, challengeSignature });
    },
    async storeDeviceEntry(cid, entry, deviceEncryptionKey) {
        await deviceStorage.encryptAndStoreKey(
            cid, entry.keyId, aes.fromBase64(entry.privateKey), deviceEncryptionKey,
            {
                algorithm: entry.algorithm,
                keyIndex: entry.keyIndex ?? 0,
                status: entry.status ?? 'active',
            },
        );
    },
    async storeTotpSecret(secret) {
        const challengeSignature = await signStepUp('totp-manage');
        await accountApi.putTotpKey({ secret: base32Encode(secret), challengeSignature });
    },
    async readWalletsBlob(root) {
        const row = await readCidBlob<unknown>(BITCASH_WALLETS_BLOB, walletBlobCalls(root));
        return row === null
            ? null
            : { doc: decodeWalletsDocument(row.value), generation: row.generation };
    },
    async writeWalletsBlob(root, doc, ifGeneration) {
        await writeCidBlob(BITCASH_WALLETS_BLOB, doc, ifGeneration, walletBlobCalls(root));
    },
};

/**
 * Writers for an ORGANIZATION's own bag: PUT /api/organization/{orgCid}/keybag,
 * whose challenge is signed with the organization's `main_signing_key` — the
 * wizard holds it in memory, the session belongs to the delegate. There is no
 * org auth-hash write — the throwaway the wizard set is RETIRED instead, by the
 * same organization-signed step-up (docs/cid-governance.md M15) — and no device
 * copy (the device belongs to the delegate), so both of those throw if a caller
 * ever asks for them.
 *
 * The Recovery Kit entry is the exception that proves the pattern: unlike the
 * auth hash and the Authenticator Key, an ORGANIZATION does have a recovery
 * kit, and its set belongs in the ORG bag these deps write (§16.6). Nothing
 * extra is needed here — the entry rides the bag.
 */
export function orgFinalizeDeps(
    orgCid: string, orgSigningPrivateKeyB64: string,
): FinalizeCredentialsDeps {
    return {
        // An organization's wallet stays in the ORG bag (§5.1, D14).
        walletCustody: 'bag',
        async replaceKeyBag(encrypted) {
            const { challenge } = await accountApi.getStepUpChallenge('org-replace-keybag');
            const message = aes.encodeUtf8(`org-replace-keybag\n${orgCid}\n${challenge}`);
            const wrapper = signHybrid(message, aes.fromBase64(orgSigningPrivateKeyB64));
            await organizationApi.replaceOrgKeyBag(orgCid, {
                entries: encrypted.entries,
                keySalt: encrypted.keySalt,
                challengeSignature: wrapperToJson(wrapper),
            });
        },
        setAuthHash() {
            return Promise.reject(new Error('An organization account has no user-facing password'));
        },
        async retirePassword() {
            const signature = await signOrgStepUp('org-retire-password', orgCid, aes.fromBase64(orgSigningPrivateKeyB64));
            await organizationApi.retireOrganizationPassword(orgCid, signature);
        },
        storeDeviceEntry() {
            return Promise.reject(new Error('Organization keys are not stored on the delegate device'));
        },
        storeTotpSecret() {
            // §15.1: the organization account never logs in, so it gets no
            // Authenticator Key. The founding delegate's key rides the delegate
            // half of the same ceremony.
            return Promise.reject(new Error('An organization account has no Authenticator Key'));
        },
        // `walletCustody: 'bag'` means these are never reached. They exist so a
        // future caller that flips the custody without thinking gets a loud
        // failure rather than an organization wallet filed under the DELEGATE's
        // derive root, which is the only root a session here can produce.
        readWalletsBlob() {
            return Promise.reject(new Error('An organization has no account blobs'));
        },
        writeWalletsBlob() {
            return Promise.reject(new Error('An organization has no account blobs'));
        },
    };
}

/**
 * Run the finalize sequence. See the module header for the ordering rationale
 * and for what callers keep outside it.
 */
export async function finalizeCredentials(
    params: FinalizeCredentialsParams,
    deps: FinalizeCredentialsDeps = accountFinalizeDeps,
): Promise<FinalizeCredentialsResult> {
    const status = params.onStatus ?? ((): void => { /* silent */ });

    // 1. The ceremony's XRPL key — added only when the bag has none. A bag that
    //    already carries one is a resume (or a second click after a failed
    //    set-authhash): re-minting would strand the key that is already on the
    //    server and in the recovery kit, and print an address nobody holds.
    const existing = getKeyBagEntry(params.bag, XRPL_MASTER_KEY_ID);
    const added: KeyBagEntry[] = [];
    let xrplSeedEntropy: Uint8Array | null = existing ? aes.fromBase64(existing.privateKey) : null;

    // 1b. The Recovery Kit's set (§16.6), under the same resume rule. Checked
    //     against the bag as passed in, so a resumed finalize adopts the set the
    //     server already holds and the SAME sheets print again.
    let recoveryKitEntry = activeRecoveryKitEntry(params.bag);
    if (!recoveryKitEntry && params.recoveryKitEntry) {
        if (params.recoveryKitEntry.keyId !== RECOVERY_KIT_KEY_ID) {
            throw new Error(
                `recoveryKitEntry must carry keyId '${RECOVERY_KIT_KEY_ID}', got `
                + `'${params.recoveryKitEntry.keyId}'`);
        }
        recoveryKitEntry = params.recoveryKitEntry;
        added.push(recoveryKitEntry);
    }

    // 1c. The ceremony's bitcash wallet (registration-wallets §4.8), under the
    //     same resume rule. Validated HERE, before the bag is written: a
    //     malformed key must fail with nothing written, not between the bag and
    //     the password — and that holds for both custody paths, since the blob
    //     write in step 3b is deliberately not allowed to fail anything.
    const walletLabel = params.bitcashWalletLabel ?? DEFAULT_BITCASH_WALLET_LABEL;
    let ceremonyWalletKey: string | null = null;
    if (params.bitcashWalletKey) {
        ceremonyWalletKey = toCompact(params.bitcashWalletKey);
        if (!verifyWalletKey(ceremonyWalletKey)) {
            throw new Error('The bitcash wallet key minted by the ceremony is not a valid wallet key');
        }
    }

    //     The ORGANIZATION path files it as a bag entry (§5.1, D14). A REVOKED
    //     wallet does not count as an active one — the member abandoned it — so
    //     the new key takes the next free slot rather than overwriting it.
    let bitcashWallet: { key: string; keyIndex: number } | null = null;
    if (deps.walletCustody === 'bag') {
        const heldWallets = activeBitcashWalletEntries(params.bag);
        if (heldWallets.length > 0) {
            bitcashWallet = {
                key: decodeBitcashWalletEntry(heldWallets[0].privateKey).key,
                keyIndex: heldWallets[0].keyIndex ?? 0,
            };
        } else if (ceremonyWalletKey) {
            const keyIndex = nextBitcashWalletKeyIndex(params.bag);
            added.push(bitcashWalletBagEntry({
                v: 1,
                key: ceremonyWalletKey,
                label: walletLabel,
                createdAt: Math.floor(Date.now() / 1000),
                source: 'registration',
            }, keyIndex));
            bitcashWallet = { key: ceremonyWalletKey, keyIndex };
        }
    }

    // 1d. The account's derive root (cid-encrypted-storage §5.1). Every account
    //     needs one, it is pure randomness with nothing to print and nothing to
    //     coordinate, and minting it here is free: the bag is being written
    //     anyway and the supersecret is already in hand. Minting it LATER is
    //     not free — the first `derive` from any module would prompt, and
    //     because a bag write is never propagated to the account's other
    //     devices, every other enrolled device would be forced into a
    //     supersecret unlock at its next login (§3.4).
    //
    //     The resume rule is stricter than `getKeyBagEntry`'s "newest active":
    //     ANY entry counts, revoked included. A second root would silently
    //     become the one `keys.getPrivateKey` returns, and every key and every
    //     blob derived from the first would stop being reproducible. Replacing
    //     the root is the owner-run estate sweep (§4.2), never a side effect of
    //     a resumed finalize.
    const hasDeriveRoot = params.bag.entries.some(e => e.keyId === DERIVE_ROOT_KEY_ID);
    if (!hasDeriveRoot) {
        added.push({
            keyId: DERIVE_ROOT_KEY_ID,
            algorithm: DERIVE_ROOT_ALGORITHM,
            privateKey: aes.toBase64(generateDeriveRoot()),
            keyIndex: 0,
            status: 'active',
        });
    }

    if (!existing && params.xrplSeedEntropy) {
        if (params.xrplSeedEntropy.length !== XRPL_SEED_ENTROPY_LEN) {
            throw new Error(`XRPL seed entropy must be exactly ${XRPL_SEED_ENTROPY_LEN} bytes`);
        }
        added.push({
            keyId: XRPL_MASTER_KEY_ID,
            algorithm: XRPL_MASTER_ALGORITHM,
            privateKey: aes.toBase64(params.xrplSeedEntropy),
            keyIndex: 0,
            status: 'active',
        });
        xrplSeedEntropy = params.xrplSeedEntropy;
    }

    const bag: KeyBag = { entries: [...params.bag.entries, ...added] };

    // Validate the Authenticator Key up front: a wrong width must fail before
    // anything has been written, not between the bag and the password.
    const totpSecret = params.totpSecret ?? null;
    if (totpSecret && totpSecret.length !== TOTP_SECRET_LENGTH) {
        throw new Error(`Authenticator Key secret must be exactly ${TOTP_SECRET_LENGTH} bytes`);
    }

    // 2. Re-encrypt under the final supersecret and replace the stored bag.
    status('Re-encrypting your keys under the final supersecret...');
    const encrypted = await encryptKeyBag(bag, params.supersecret);
    await deps.replaceKeyBag(encrypted);

    // 3. Device copy of what step 1 added — after the server accepted the bag,
    //    so the device never holds a key the account does not have.
    const deviceStoredKeyIds: string[] = [];
    let deviceCopyMissing = false;
    // §16.5: `recovery_kit` never reaches device storage — its coefficients
    // plus one printed sheet ARE the supersecret. It is the only id left on
    // that list since personal wallets moved off the bag. Filtering here rather
    // than inside the loop also keeps `deviceCopyMissing` honest: a finalize
    // that added ONLY that has no device gap to report.
    const deviceCopyable = added.filter(e => isDeviceStorableKeyId(e.keyId));
    // A device partition belongs to ONE account. The organization path finalizes
    // the ORG's bag while the device belongs to the founding delegate, so its
    // `device.cid` names a different account — copying there would file the
    // organization's `derive_root` under the delegate's own `derive_root` and
    // the delegate would derive every key from the wrong root after a PIN
    // unlock. A mismatch is "this device holds no keys for this account", which
    // is not a gap to warn about. Every production caller passes a matching
    // pair; this is the guard that keeps a future one honest.
    const deviceForThisAccount = params.device && params.device.cid === params.cid
        ? params.device
        : null;
    const deviceBelongsElsewhere = Boolean(params.device) && deviceForThisAccount === null;
    // §7.3: the member's per-device selection. Applied only where we HAVE a
    // device (and therefore a manifest) — the no-device branch below still
    // warns about the whole set, as it did before selective import existed.
    const deviceWanted = deviceForThisAccount
        ? deviceCopyable.filter(e => isDeviceKeyChosen(deviceForThisAccount.manifest ?? null, e.keyId))
        : deviceCopyable;
    if (deviceWanted.length > 0 && !deviceBelongsElsewhere) {
        if (deviceForThisAccount) {
            status('Storing the new key on this device...');
            for (const entry of deviceWanted) {
                try {
                    await deps.storeDeviceEntry(
                        deviceForThisAccount.cid, entry, deviceForThisAccount.encryptionKey);
                    deviceStoredKeyIds.push(entry.keyId);
                } catch (err) {
                    // Non-fatal by design: the key is safely in the server bag
                    // and on paper. Losing the device copy only costs a
                    // supersecret prompt the first time the key is used here.
                    console.warn('[finalize] could not store the new key on this device:', err);
                    deviceCopyMissing = true;
                }
            }
        } else {
            deviceCopyMissing = true;
        }
    }

    // 3b. The member's wallet document (cid-encrypted-storage §7.2), on the
    //     personal and delegate paths. AFTER the bag write, because the blob
    //     key is derived from the `derive_root` that write just persisted; the
    //     root is read out of the bag we assembled rather than out of
    //     `state/keys`, which the wizard loads only after this returns.
    //
    //     NOTHING HERE MAY FAIL THE SEQUENCE. A node that refuses the blob
    //     leaves the member with credentials, a printed wallet key and a
    //     warning — never with a half-finalized account.
    let walletBlobMissing = false;
    if (deps.walletCustody === 'blob') {
        const rootEntry = bag.entries.find(e => e.keyId === DERIVE_ROOT_KEY_ID);
        try {
            if (!rootEntry) throw new Error('the account bag carries no derive root');
            const root = aes.fromBase64(rootEntry.privateKey);
            const held = await deps.readWalletsBlob(root);
            const active = held ? activeWallets(held.doc) : [];
            if (active.length > 0) {
                // RESUME. The bits were released into THIS wallet.
                bitcashWallet = { key: active[0].key, keyIndex: active[0].slot };
            } else if (ceremonyWalletKey) {
                status('Saving your wallet...');
                const appended = appendWallet(held?.doc ?? emptyWalletsDocument(), {
                    key: ceremonyWalletKey,
                    label: walletLabel,
                    createdAt: Math.floor(Date.now() / 1000),
                    source: 'registration',
                    status: 'active',
                });
                await deps.writeWalletsBlob(root, appended.doc, held?.generation ?? 0);
                bitcashWallet = { key: ceremonyWalletKey, keyIndex: appended.slot };
            }
        } catch (err) {
            console.warn('[finalize] the wallet document could not be written:', err);
            walletBlobMissing = true;
            // Report the ceremony's key anyway: the money leg releases the
            // voucher's bits into whatever this names and the kit prints it, so
            // reporting null would leave the member with a funded wallet whose
            // key exists nowhere. Slot 0 is what an account with no document
            // would have been given.
            if (!bitcashWallet && ceremonyWalletKey) {
                bitcashWallet = { key: ceremonyWalletKey, keyIndex: 0 };
            }
        }
    }

    // 4. The final password — last of the credential writes, because it kills
    //    the throwaway. The organization's registration installs none: it
    //    RETIRES the throwaway instead (docs/cid-governance.md M15), in the same place.
    if (params.password !== undefined) {
        status('Setting your final password...');
        await deps.setAuthHash(params.password, params.cid);
    } else if (params.retirePassword) {
        if (!deps.retirePassword) throw new Error('These writers cannot retire a password');
        status('Retiring the organization\'s working password...');
        await deps.retirePassword();
    }

    // 5. The Authenticator Key. NEVER twice for the same registration: a second
    //    PUT rotates the secret on the node and voids the sheet already printed
    //    (§15.8), so a resume passes `totpAlreadyStored` and this is skipped
    //    while still reporting the key as stored.
    let totpStored = false;
    if (totpSecret) {
        if (params.totpAlreadyStored) {
            totpStored = true;
        } else {
            status('Storing your Authenticator Key...');
            await deps.storeTotpSecret(totpSecret);
            totpStored = true;
        }
    }

    return {
        bag, xrplSeedEntropy, xrplMinted: added.some(e => e.keyId === XRPL_MASTER_KEY_ID),
        deriveRootMinted: !hasDeriveRoot,
        recoveryKitEntry, bitcashWallet, walletBlobMissing,
        deviceStoredKeyIds, deviceCopyMissing, totpStored,
    };
}
